import { PopUp } from './PopUp'
import { Options } from './types'
import { debug } from './logger'
import { dispatchEnrichDataEvent, isValidEmail } from './utils'

const EMAIL_INPUT_DEBOUNCE_MS = 1000

const asInputElement = (target: EventTarget | null | undefined): HTMLInputElement | undefined => {
  const node = target as Node | null | undefined
  return node?.nodeName === 'INPUT' ? (node as HTMLInputElement) : undefined
}

export class PrelaunchApi {
  form?: HTMLFormElement
  routingId?: string
  fieldNamesToEnrich?: string[]
  emailFieldName?: string
  enrichmentParentSelector?: string
  CPPopUp?: PopUp
  options?: Options
  emailInputDebounceTimer?: ReturnType<typeof setTimeout>
  lastDispatchedEmail?: string
  formDocumentWithListeners?: Document
  constructor(form?: HTMLFormElement, CPPopUp?: PopUp, options?: Options) {
    this.setForm(form)
    this.setCPPopUp(CPPopUp)
    this.addPrelaunchListeners()
    this.options = options
  }

  setCPPopUp = (CPPopUp?: PopUp) => {
    this.CPPopUp = CPPopUp
  }

  setForm = (form?: HTMLFormElement) => {
    this.form = form
    // hideEnrichmentFields below re-hides and wipes the enrichment fields,
    // so the same email must become dispatchable again: the replayed
    // enrichment response is what re-fills them (e.g. HubSpot form
    // shortening, where the full form mounts ~800ms after the email blur
    // already dispatched)
    this.lastDispatchedEmail = undefined
    this.syncFormDocumentListeners()
    this.controlEmailField()
    this.hideEnrichmentFields()
  }

  initPrelaunch = ({
    fieldsToEnrich = [],
    emailField,
  }: {
    fieldsToEnrich: string[]
    emailField?: string
  }) => {
    this.emailFieldName = emailField
    this.fieldNamesToEnrich = fieldsToEnrich
    // A (re)booting iframe has no prelaunch state yet — e.g. after a popup
    // close rebuilds it — so the same email must be dispatchable to it again
    this.lastDispatchedEmail = undefined
    this.syncFormDocumentListeners()
    this.controlEmailField()
    this.hideEnrichmentFields()
  }

  // Single funnel for the automatic PERFORM_PRELAUNCH paths: dedupes by
  // value (normalized, so padding can't split the key) — blur, autofill and
  // the initial-value dispatch can all fire for the same email, and every
  // duplicate makes the booking app replay the enrichment response. The
  // wire value itself is trimmed centrally in dispatchEnrichDataEvent
  dispatchEmail = (rawValue?: string): void => {
    const email = rawValue?.trim()
    if (!email || email === this.lastDispatchedEmail) {
      return
    }
    if (dispatchEnrichDataEvent(email)) {
      this.lastDispatchedEmail = email
    }
  }

  // Form tools like HubSpot destroy and re-create the form DOM when a form
  // pop-up is closed and reopened (or on re-render). Their forms keep a
  // stable id across re-creation (hsForm_<guid>, mktoForm_<id>, …), so when
  // the node we hold is detached we can find its replacement by id.
  // Duplicate ids on a page (invalid HTML) resolve to the first match per
  // getElementById semantics and are not defended against
  getFormDocument = (): Document => this.form?.ownerDocument ?? document

  getLiveForm = (): HTMLFormElement | null => {
    if (!this.form) {
      return null
    }
    if (this.form.isConnected) {
      return this.form
    }
    if (this.form.id) {
      const replacement = this.getFormDocument().getElementById(this.form.id)
      if (replacement instanceof HTMLFormElement) {
        return replacement
      }
      debug(
        `Tracked form is detached and no form with id "${this.form.id}" exists — falling back to document-wide matching`
      )
    }
    return null
  }

  // Scoped to the live form whenever we can identify it; document-wide only
  // as a last resort for re-created forms without a stable id
  getSearchRoot = (): HTMLFormElement | Document | undefined => {
    if (!this.form) {
      return undefined
    }
    return this.getLiveForm() ?? this.getFormDocument()
  }

  controlEmailField = () => {
    const root = this.getSearchRoot()
    if (this.emailFieldName && root) {
      const emailField = root.querySelector<HTMLInputElement>(
        `input[name="${this.emailFieldName}"]`
      )
      if (emailField) {
        debug(`Email field found: ${emailField.name}`)
        // If the email field has an initial value, dispatch the enrich data event
        this.dispatchEmail(emailField.value)
      } else {
        debug(`Email field with name ${this.emailFieldName} not found`)
      }
    } else {
      debug('No email field name or form found')
    }
  }

  getEmailFieldTarget = (event: Event): HTMLInputElement | undefined => {
    // event.target covers the common light-DOM case without composedPath()'s
    // per-event array allocation. For forms inside open shadow roots the
    // target is retargeted to the shadow host by the time the event reaches
    // the document listener — never an input, since inputs can't host shadow
    // roots — so composedPath()[0] recovers the real element. IE 11 has no
    // composedPath but no shadow DOM either. Forms inside closed shadow
    // roots are not supported
    const target = asInputElement(event.target) ?? asInputElement(event.composedPath?.()[0])
    if (!target) {
      return undefined
    }
    if (!this.emailFieldName || target.name !== this.emailFieldName) {
      return undefined
    }
    // Only react to the tracked form's email field — pages commonly have
    // other inputs with the same name (newsletter footers, sibling forms).
    // When the live form cannot be identified (re-created form without a
    // stable id) we fall back to matching by name alone
    const liveForm = this.getLiveForm()
    if (liveForm && target.form !== liveForm) {
      return undefined
    }
    // In the name-only fallback mode, only the registered instance may
    // dispatch: pages can run multiple MarketingApi instances (multi-form
    // integrations, SPA re-inits), and every instance that lost track of its
    // form would otherwise match the same event and dispatch duplicates
    if (!liveForm && window.ChiliPiper?._marketingApi?.prelaunch !== this) {
      return undefined
    }
    return target
  }

  // Blur is captured on the document instead of being bound to the input node:
  // a node-bound handler is silently lost when the form tool re-creates its
  // inputs, which left enrichment untriggered until submit
  onEmailFieldBlur = (event: Event) => {
    const target = this.getEmailFieldTarget(event)
    if (target) {
      clearTimeout(this.emailInputDebounceTimer)
      this.dispatchEmail(target.value)
    }
  }

  // Keystrokes, IME composition and paste produce InputEvents with a specific
  // inputType (insertText, insertCompositionText, insertFromPaste, …), while
  // browser autofill dispatches a plain event with no inputType (Safari uses
  // insertReplacementText) — that difference is how we tell the two apart.
  // Known ambiguity: Safari also uses insertReplacementText for autocorrect
  // and accepted text suggestions during typing, indistinguishable from its
  // AutoFill — an accepted mid-typing suggestion can therefore dispatch after
  // the debounce; the value dedupe in dispatchEmail keeps the later blur from
  // producing a second request
  isAutofillEvent = (event: Event): boolean => {
    const { inputType } = event as InputEvent
    if (!inputType) {
      return true
    }
    return inputType === 'insertReplacementText'
  }

  // Browser autofill fills every field at once without focusing them, so blur
  // never fires on the email input — listen for input too and dispatch once
  // an autofilled valid email settles. Values typed by the guest are
  // deliberately left to the blur handler so a slow typer doesn't trigger an
  // enrichment call for every momentarily-valid value
  onEmailFieldInput = (event: Event) => {
    const target = this.getEmailFieldTarget(event)
    if (!target) {
      return
    }
    // Any edit — including a programmatic overwrite — cancels a pending
    // autofill dispatch: the value it captured no longer reflects the field
    clearTimeout(this.emailInputDebounceTimer)
    // onEnrichData re-emits input events on the fields it writes so form
    // tools pick up the values; reacting to our own write would loop
    // prelaunch forever when the email field is itself mapped for enrichment
    if (target.hasAttribute('data-enriched')) {
      return
    }
    const { value } = target
    if (!this.isAutofillEvent(event) || !isValidEmail(value)) {
      return
    }
    this.emailInputDebounceTimer = setTimeout(() => {
      this.dispatchEmail(value)
    }, EMAIL_INPUT_DEBOUNCE_MS)
  }

  queryFieldsToEnrich = (): NodeListOf<HTMLElement> | undefined => {
    const root = this.getSearchRoot()
    if (!root || !this.fieldNamesToEnrich?.length) {
      return undefined
    }
    const fieldsToEnrichSelector = this.fieldNamesToEnrich.reduce((acc, fieldId) => {
      // This Selector will match the inputs that have the fieldId as a exact match
      // /and its corresponding labels (on HS, the input has fieldId as name
      // and the input id has the fieldId as preffix, so we need to match labels with preffix of `fieldId-`)
      return `${acc ? `${acc}, ` : ''}label[for^="${fieldId}-"], label[for="${fieldId}"], input[id="${fieldId}"], input[name="${fieldId}"], select[id="${fieldId}"], select[name="${fieldId}"]`
    }, '')
    debug('Selector to find fields to enrich: ', fieldsToEnrichSelector)
    return root.querySelectorAll<HTMLElement>(fieldsToEnrichSelector)
  }

  // Here we will find the input and label elements to hide
  // And send to booking app the email value once it gets blurred
  hideEnrichmentFields = () => {
    const fieldElementsToEnrich = this.queryFieldsToEnrich()
    if (fieldElementsToEnrich) {
      fieldElementsToEnrich.forEach(field => {
        if (this.enrichmentParentSelector) {
          debug('Enrichment parent selector: ', this.enrichmentParentSelector)
          const div = field.closest<HTMLDivElement>(this.enrichmentParentSelector)
          if (div) {
            debug('Hiding enrichment parent element: ', div)
            div.style.display = 'none'
          }
        }
        debug('Hiding field: ', field)
        field.style.display = 'none'
        if ('value' in field) {
          field.value = ''
        }
      })
    } else {
      debug('Form not found or no fields to enrich are set')
    }
  }

  // When the enrichment API is called with the email value
  // This function properly shows back or keeps fields hidden
  onEnrichData = ({
    enrichedFields,
    routingId,
  }: {
    enrichedFields: Record<string, string>
    routingId?: string
  }) => {
    debug('Enriching data: ', enrichedFields)
    this.routingId = routingId
    // Re-resolve the fields at response time — the form may have been
    // re-created since they were hidden. When none of the fields exist in the
    // DOM (e.g. the response landed mid re-render), skip the updates entirely:
    // writing to the cached, now-detached nodes serves no one
    const freshFieldElements = this.queryFieldsToEnrich()
    if (freshFieldElements && !freshFieldElements.length) {
      debug('No enrichment fields found in the DOM at response time, skipping field updates')
      return
    }
    freshFieldElements?.forEach(field => {
      const fieldValue = (() => {
        if ('name' in field && typeof field.name === 'string') {
          return enrichedFields[field.name]
        }
        // We should show bakc labels too
        if (field.hasAttribute('for')) {
          const fieldId = Object.keys(enrichedFields).find(fieldId =>
            field.getAttribute('for')?.includes(fieldId)
          )
          return fieldId ? enrichedFields[fieldId] : undefined
        }
        return enrichedFields[field.id]
      })()
      // Adds the value to the field so third party validations work
      if ('value' in field) {
        field.value = fieldValue || ''
        field.setAttribute('data-enriched', 'true')
        // Since some form tools listens to this event to update their instance values, we need to dispatch it
        field.dispatchEvent(new Event('input', { bubbles: true }))
      }

      if (field.hasAttribute('required')) {
        field.setAttribute('data-required', 'true')
        field.removeAttribute('required')
      }

      // If we couldn't enrich the data, we show it back
      if (!fieldValue) {
        if (this.enrichmentParentSelector) {
          const div = field.closest<HTMLDivElement>(this.enrichmentParentSelector)
          if (div) {
            div.style.display = ''
          }
        }
        field.style.display = ''

        // If the field is required, we need to put it back as required
        if (field.hasAttribute('data-required')) {
          field.setAttribute('required', 'true')
          field.removeAttribute('data-required')
        }

        if (field.hasAttribute('data-enriched')) {
          field.removeAttribute('data-enriched')
        }
      } else {
        if (this.enrichmentParentSelector) {
          const div = field.closest<HTMLDivElement>(this.enrichmentParentSelector)
          if (div) {
            div.style.display = 'none'
          }
        }
        field.style.display = 'none'

        // If the field is required, we need to remove required as it was enriched and no value is there
        if (field.hasAttribute('required')) {
          field.setAttribute('data-required', 'true')
          field.removeAttribute('required')
        }
      }
    })
  }

  onMessage = (event: MessageEvent) => {
    if (event.data.action === 'init-prelaunch') {
      this.initPrelaunch(event.data.args)
      this.options?.onLoadRouter?.(event.data.args.fieldsToEnrich)
    } else if (event.data.action === 'prelaunch-succeeded') {
      this.onEnrichData(event.data.args)
      this.options?.onEnrichData?.(event.data.args)
    }
  }

  // No 'change' listener on purpose: autofill always fires 'input', while
  // 'change' is a plain Event even for typed values, so it would defeat the
  // typing-vs-autofill distinction and schedule dispatches for typed input
  getEmailFieldListeners = (): Array<[string, (event: Event) => void]> => [
    ['blur', this.onEmailFieldBlur],
    ['input', this.onEmailFieldInput],
  ]

  syncFormDocumentListeners = () => {
    const formDocument = this.getFormDocument()
    const nextRoot = formDocument === document ? undefined : formDocument
    if (nextRoot === this.formDocumentWithListeners) {
      return
    }
    this.removeFormDocumentListeners()
    if (nextRoot) {
      this.getEmailFieldListeners().forEach(([type, handler]) =>
        nextRoot.addEventListener(type, handler, true)
      )
      this.formDocumentWithListeners = nextRoot
      debug('Prelaunch listeners attached to the embedded form document')
    }
  }

  removeFormDocumentListeners = () => {
    const root = this.formDocumentWithListeners
    if (!root) {
      return
    }
    this.getEmailFieldListeners().forEach(([type, handler]) =>
      root.removeEventListener(type, handler, true)
    )
    this.formDocumentWithListeners = undefined
  }

  addPrelaunchListeners = () => {
    window.addEventListener('message', this.onMessage, false)
    this.getEmailFieldListeners().forEach(([type, handler]) =>
      document.addEventListener(type, handler, true)
    )
    this.syncFormDocumentListeners()
  }

  removeEventListeners = () => {
    window.removeEventListener('message', this.onMessage)
    this.getEmailFieldListeners().forEach(([type, handler]) =>
      document.removeEventListener(type, handler, true)
    )
    this.removeFormDocumentListeners()
    clearTimeout(this.emailInputDebounceTimer)
  }
}
